/** Page Profil : infos du compte + changement de mot de passe. */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AtSign, KeyRound, LogOut, UserCircle2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { authApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';

export function ProfilePage() {
  const { logout } = useAuth();
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => authApi.me(),
  });

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (vars: { current: string; next: string }) =>
      authApi.changePassword(vars.current, vars.next),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (next.length < 8) return setError('Mot de passe trop court (8 caractères minimum)');
    if (next !== confirm) return setError('Les deux mots de passe ne correspondent pas');
    try {
      await mutation.mutateAsync({ current, next });
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  return (
    <div>
      <Header title="Mon profil" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Infos compte */}
        <Card title="Identité">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-accent-soft flex items-center justify-center">
              <UserCircle2 size={28} className="text-accent" />
            </div>
            <div>
              <p className="font-semibold text-ink">{me?.username ?? '—'}</p>
              <p className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                <AtSign size={12} /> {me?.email ?? '—'}
              </p>
            </div>
          </div>
          <div className="pt-4 border-t border-stone-100">
            <Button variant="secondary" size="sm" onClick={logout}>
              <LogOut size={13} className="mr-1" /> Se déconnecter
            </Button>
          </div>
        </Card>

        {/* Changement de mot de passe */}
        <Card
          title="Changer mon mot de passe"
          action={<KeyRound size={14} className="text-stone-400" />}
        >
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              label="Mot de passe actuel"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
            <Input
              label="Nouveau mot de passe (min. 8 caractères)"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
            />
            <Input
              label="Confirmer le nouveau mot de passe"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
            {error && (
              <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded-md p-2">
                {error}
              </div>
            )}
            {success && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md p-2">
                Mot de passe mis à jour avec succès.
              </div>
            )}
            <Button type="submit" loading={mutation.isPending} className="w-full">
              Mettre à jour le mot de passe
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
